import type { ReactNode } from 'react'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'
import { cn } from '@/utils'

/** Width of the Any / Yes / No (and orientation) joined control. */
const SEGMENT_GROUP_WIDTH_CLASS = 'w-[136px]'

export type TrayFilterSegmentOption<T extends string> = {
  value: T
  ariaLabel: string
  label?: string
  icon?: ReactNode
}

type TrayFilterSegmentRowProps<T extends string> = {
  label: string
  value: T
  options: ReadonlyArray<TrayFilterSegmentOption<T>>
  onChange: (value: T) => void
  className?: string
  /** Overrides the joined control's own width — defaults to `SEGMENT_GROUP_WIDTH_CLASS` (136px),
   *  shared by every right-panel tray's Orientation/Placed/Tagged/Favorited-style rows. A caller
   *  matching a different Figma spec (e.g. ClikArt's Placed row at 170px, or Backgrounds' Size/
   *  Placed rows at 160px for the longer "Spread" label) overrides it here rather than forking
   *  this component. */
  groupClassName?: string
  /** Overrides each segment's own padding — defaults to none (centered by the shared Toggle base
   *  styles). Set when the target design specifies explicit per-segment padding instead. */
  itemClassName?: string
}

/**
 * Label plus a joined 3-segment control, shared by every right-panel tray's segment filters
 * (Photos: Orientation / Placed / Tagged / Favorited; ClikArt: Placed; Backgrounds: Size / Placed;
 * Templates: Favorited).
 */
export function TrayFilterSegmentRow<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  groupClassName,
  itemClassName,
}: TrayFilterSegmentRowProps<T>) {
  return (
    <div className={cn('flex w-full items-center justify-between', className)}>
      {/* `min-w-fit`, not `min-w-0`: the label never wraps/truncates, so letting flexbox shrink
          it below its own text width just lets the text render past its box and overlap the
          segmented control next to it once that control's width grows past the 136px default
          (e.g. Backgrounds' "Spread" option via `groupClassName`). */}
      <span className="min-w-fit flex-1 px-2 py-1.5 text-sm font-medium leading-5 text-foreground">
        {label}
      </span>
      <ToggleGroup
        type="single"
        value={value}
        onValueChange={(next) => {
          if (next) onChange(next as T)
        }}
        variant="outline"
        className={cn(
          'inline-flex h-9 shrink-0 items-center justify-center gap-0',
          SEGMENT_GROUP_WIDTH_CLASS,
          groupClassName
        )}
        aria-label={label}
      >
        {options.map((option, index) => {
          const isFirst = index === 0
          const isLast = index === options.length - 1

          return (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              aria-label={option.ariaLabel}
              className={cn(
                'h-9 min-w-0 flex-1 basis-0 rounded-none border border-input bg-background px-0 text-sm font-medium shadow-xs',
                'hover:bg-muted hover:text-foreground',
                'data-[state=on]:bg-accent data-[state=on]:text-accent-foreground data-[state=on]:hover:bg-accent',
                'focus-visible:z-10',
                isFirst && 'rounded-l-md',
                isLast && 'rounded-r-md',
                !isFirst && '-ml-px',
                itemClassName
              )}
            >
              {option.icon ?? option.label}
            </ToggleGroupItem>
          )
        })}
      </ToggleGroup>
    </div>
  )
}
